import { withNamespaces } from "react-i18next";
import { Alert, Button, ButtonDropdown, Col, DropdownItem, DropdownMenu, DropdownToggle, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import Spinner from 'react-bootstrap/Spinner';
import Select from "react-select";
import { connect } from 'react-redux';

import SupplierTeamService from "src/modules/3rd-party-management/apis/common/SupplierTeamService";
import { AvField, AvForm, AvInput } from "availity-reactstrap-validation";

import { EMPTY_LIST } from "src/common/constants";
import LocalizedPhoneInput from "src/modules/3rd-party-management/components/LocalizedPhoneInput";
import { isFeatureEnabledForActiveModule } from "src/helpers/module_helper";
import AIFieldLabel from "src/modules/3rd-party-management/components/AIFieldLabel";

const LoadingComponent = () => {
    return (
        <div className="contact-people pt-0">
            <div className="list">
                <div className="item">
                    <Row className="mb-2">
                        <Col sm="12" md="4">
                            <Label className="form-label">
                                <div style={{width: '80px'}} className="dt-field dt-skeleton"></div>
                            </Label>
                            <p>
                                <div className="dt-field dt-skeleton"></div>
                            </p>
                        </Col>

                        <Col sm="12" md="4">
                            <Label className="form-label">
                                <div style={{width: '80px'}} className="dt-field dt-skeleton"></div>
                            </Label>
                            <p>
                                <div className="dt-field dt-skeleton"></div>
                            </p>
                        </Col>

                        <Col sm="12" md="4">
                            <Label className="form-label">
                                <div style={{width: '80px'}} className="dt-field dt-skeleton"></div>
                            </Label>
                            <p>
                                <div className="dt-field dt-skeleton"></div>
                            </p>
                        </Col>
                    </Row>

                    <Row>
                        <Col sm="12" md="4">
                            <Label className="form-label">
                                <div style={{width: '80px'}} className="dt-field dt-skeleton"></div>
                            </Label>
                            <p>
                                <div className="dt-field dt-skeleton"></div>
                            </p>
                        </Col>

                        <Col sm="12" md="4">
                            <Label className="form-label">
                                <div style={{width: '80px'}} className="dt-field dt-skeleton"></div>
                            </Label>
                            <div className="dt-field dt-skeleton"></div>
                        </Col>
                    </Row>

                    <Row>
                        <Col sm="12" className="d-flex justify-content-end">
                            <div className="dt-field dt-skeleton"></div>
                        </Col>
                    </Row>
                </div>
            </div>
        </div>
    )   
}

const ManagementTeam = ({
    t,
    supplierId,
    Organization,
    readOnly = false
}) => {
    const [ showPersonModal, setShowPersonModal ] = useState(false);

    const [ personToDelete, setPersonToDelete ] = useState(null);

    const [ editingPersonValues, setEditingPersonValues ] = useState(null);

    const [ personTitle, setPersonTitle ] = useState({
        show    :   false,
        value   :   'Mr.'
    });

    const avFormRef = useRef();

    const {
        data: team,
        refetch: refetchTeam,
        isFetching: teamIsFetching
    } = useQuery({
		queryKey: [
            '3rd-party-management-fetch-supplier-team-list',
            supplierId
        ],
		queryFn: async () => {
			const service = SupplierTeamService.getInstance();

            return await service.list(supplierId, {});
		},
		cacheTime: 0,
		refetchOnWindowFocus: false,
		onError: () => {
			toast(t('An error occurred while fetching management team list.'), {
				type: 'error',
			});
		},
	});

    const handleCreateSupplierNewTeamPersonMutation = useMutation({
        mutationFn: async (payload) => {
            const service = SupplierTeamService.getInstance();

            return await service.create(payload);
        },
        onSuccess: () => {
            refetchTeam();

            toast(t("New person created successfully."), {
                type: "success",
            });

            togglePersonModal();
        },
        onError: () => {
            toast(t("An error occurred while creating new person."), {
                type: "error",
            });
        }
    });

    const handleUpdateSupplierTeamPersonMutation = useMutation({
        mutationFn: async ({
            personId,
            payload
        }) => {
            const service = SupplierTeamService.getInstance();
            return await service.update(supplierId, personId, payload);
        },
        onSuccess: () => {
            refetchTeam();
            toast(t("Person updated successfully."), {
                type: "success",
            });
            setEditingPersonValues(null);
            togglePersonModal();
        },
        onError: () => {
            toast(t("An error occurred while updating person."), {
                type: "error",
            });
        }
    });

    const handleDeleteSupplierProductMutation = useMutation({
        mutationFn: async (personId) => {
            const service = SupplierTeamService.getInstance();
            return await service.delete(supplierId, personId);
        },
        onSuccess: () => {
            setPersonToDelete(null);
            refetchTeam();

            toast(t("Team person deleted successfully."), {
                type: "success",
            });
        },
        onError: () => {
            toast(t("An error occurred while deleting team person."), {
                type: "error",
            });
        }
    });

    const {
        isLoading   :   checkingSanctionsInProccess,
        mutate      :   mutateSanctionsCheck
    } = useMutation({
        mutationFn: async () => {
            const service = SupplierTeamService.getInstance();
            return await service.checkSanctions(supplierId);
        },
        onSuccess: (data) => {
            toast(t("Number of found sanction items") + `: ${data?.countOfSanctions || 0}`, {
                type: "info",
            });
        },
        onError: () => {
            toast(t("An error occurred while checking sanctions."), {
                type: "error",
            });
        }
    });

    const getLanguageName = (id) => t(
        ((Organization?.languages || []).find((lng) => lng?.language?.id === id))?.name
    )

    const togglePersonModal = () => setShowPersonModal(!showPersonModal);

    const handleCreationModalSubmit = (e, values) => {
        if(editingPersonValues && editingPersonValues.personId){
            handleUpdateSupplierTeamPersonMutation.mutate({
                personId    :   editingPersonValues.personId,
                payload     :   values['person']
            });
        }
        else{
            handleCreateSupplierNewTeamPersonMutation.mutate({
                ...values['person'],
                'supplier'      :   supplierId
            });
        }
    }

    const showTeamPersonEditingForm = (person) => {
        setPersonTitle({
            show    :   false,
            value   :   person.title
        });

        setEditingPersonValues({
            'person[title]'         :   person.title,
            'person[name]'          :   person.name,
            'person[lastName]'      :   person.lastName,
            'person[localLanguageFirstName]' : person.localLanguageFirstName,
            'person[localLanguageLastName]'  : person.localLanguageLastName,
            'person[position]'      :   person.position,
            'person[language]'      :   person.language,
            'person[email]'         :   person.email,
            'person[phoneNumber]'   :   person.phoneNumber,
            'personId'              :   person.id
        });
    }

    const renderCreateAndEditModals = () => {
        const personFormInProcess = (
            handleCreateSupplierNewTeamPersonMutation.isFetching || handleCreateSupplierNewTeamPersonMutation.isLoading
        ) || (
            handleUpdateSupplierTeamPersonMutation.isFetching || handleUpdateSupplierTeamPersonMutation.isLoading
        );

        const languages = (Organization?.languages || []).map((lng) => {
            return {
                value   :   lng?.language?.id,
                label   :   t(lng.name),
                code    :   lng.language_code,
            }
        });

        return (
            <Modal isOpen={ showPersonModal } onClosed={() => {
                editingPersonValues && setEditingPersonValues(null);
            }} backdrop="static" keyboard={ false } size="xl" className="contact-person-creation-modal">
                
                <AvForm ref={ avFormRef } onValidSubmit={ handleCreationModalSubmit } className="needs-validation m-2" model={ editingPersonValues }>
                    <ModalHeader toggle={ togglePersonModal }>
                        {t(editingPersonValues?.personId ? "Edit Person" : "Add A New Person")}
                    </ModalHeader>

                    <ModalBody>
                        <Row className="mb-3">
                            <Col md="6" sm="12">
                                <Label for="new-team-person-name">
                                    {t("First Name")}: <span className="text-danger">*</span>
                                </Label>

                                <div className="d-flex flex-wrap position-relative">
                                    <ButtonDropdown className="position-absolute" 
                                        isOpen={ personTitle.show } 
                                        toggle={() => {
                                            setPersonTitle((currentState) => {
                                                return {
                                                    ...currentState,
                                                    show    :   !currentState.show
                                                }
                                            });
                                        }} 
                                        style={{
                                            left: "0px",
                                            top: "0px",
                                            width: "74px",
                                            height: "33px"
                                        }}>
                                            <DropdownToggle outline split style={{ border: 'none', borderRight: '1px solid rgb(206, 212, 218)' }}>
                                                <span style={{marginRight: '8px'}}>
                                                    { t(personTitle.value || 'Mr.') }
                                                </span>
                                            </DropdownToggle>
                                            
                                            <DropdownMenu>
                                                <DropdownItem onClick={() => { 
                                                    setPersonTitle((currentState) => {
                                                        return {
                                                            ...currentState,
                                                            value   :   'Mr.'
                                                        }
                                                    });
                                                }}>
                                                    { t('Mr.') }
                                                </DropdownItem>

                                                <DropdownItem onClick={() => { 
                                                    setPersonTitle((currentState) => {
                                                        return {
                                                            ...currentState,
                                                            value   :   'Mrs.'
                                                        }
                                                    });
                                                }}>
                                                    { t('Mrs.') }
                                                </DropdownItem>

                                                <DropdownItem onClick={() => { 
                                                    setPersonTitle((currentState) => {
                                                        return {
                                                            ...currentState,
                                                            value   :   'Ms.'
                                                        }
                                                    });
                                                }}>
                                                    { t('Ms.') }
                                                </DropdownItem>

                                                <DropdownItem onClick={() => { 
                                                    setPersonTitle((currentState) => {
                                                        return {
                                                            ...currentState,
                                                            value   :   'Mx.'
                                                        }
                                                    });
                                                }}>
                                                    { t('Mx.') }
                                                </DropdownItem>
                                            </DropdownMenu>
                                    </ButtonDropdown>

                                    <AvInput 
                                        id={`person-title`}
                                        name={`person[title]`}
                                        type="hidden"
                                        value={ personTitle.value || 'Mr.'}
                                    />

                                    <AvField
                                        id="new-team-person-name"
                                        name={`person[name]`}
                                        type="text"
                                        errorMessage={t("This field cannot be blank")}
                                        className="form-control"
                                        validate={{
                                            required: { value: true },
                                        }}
                                        style={{
                                            paddingLeft: '85px'
                                        }}
                                        groupAttrs={{
                                            className: 'w-100'
                                        }}
                                    />
                                </div>
                            </Col>

                            <Col md="6" sm="12">
                                <Label for="new-team-person-lastname">
                                    {t("Surname")}: <span className="text-danger">*</span>
                                </Label>
                                <AvField
                                    id="new-team-person-lastname"
                                    name={`person[lastName]`}
                                    type="text"
                                    errorMessage={t("This field cannot be blank")}
                                    className="form-control"
                                    validate={{
                                        required: { value: true },
                                    }}
                                />
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md="6" sm="12">
                                <Label for="new-team-person-name-local">
                                    {t("First Name In Local Language")}:
                                </Label>
                                <AvField
                                    id="new-team-person-name-local"
                                    name={`person[localLanguageFirstName]`}
                                    type="text"
                                    className="form-control"
                                    validate={{
                                        required: { value: false },
                                    }}
                                />
                            </Col>

                            <Col md="6" sm="12">
                                <Label for="new-team-person-lastname-local">
                                    {t("Surname In Local Language")}:
                                </Label>
                                <AvField
                                    id="new-team-person-lastname-local"
                                    name={`person[localLanguageLastName]`}
                                    type="text"
                                    className="form-control"
                                    validate={{
                                        required: { value: false },
                                    }}
                                />
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md="4" sm="12">
                                <Label for="new-team-person-position">
                                    {t("Position")}
                                </Label>
                                <AvField
                                    name="person[position]"
                                    type="text"
                                    errorMessage={t("This field cannot be blank")}
                                    className="form-control"
                                    validate={{
                                        required: { value: true },
                                    }}
                                    id="new-team-person-position"
                                />
                            </Col>

                            <Col md="4" sm="12">
                                <Label for="new-team-person-language">
                                    {t("Language")}
                                </Label>
                                <Select
                                    placeholder={t("Select") + "..."}
                                    classNamePrefix="select2-selection"
                                    id='new-team-person-language'
                                    options={ languages }
                                    menuPortalTarget={ document.body }
                                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                    onChange={ (e) => {
                                        const languageInput = avFormRef.current._inputs['person[language]'];
                                        languageInput.value = e.value;
                                        languageInput.validate();
                                    } }
                                    defaultValue={editingPersonValues && {
                                        value   :   editingPersonValues['person[language]'],
                                        label   :   getLanguageName(editingPersonValues['person[language]'])
                                    }}
                                />
                                <AvField
                                    name="person[language]"
                                    type="hidden" 
                                    errorMessage={t("This field cannot be blank")}
                                    validate={{
                                        required: { value: true },
                                    }}
                                />
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md="4" sm="12">
                                <Label for="new-team-person-email">
                                    {t("Email")}
                                </Label>
                                <AvField
                                    name="person[email]"
                                    type="email"
                                    className="form-control"
                                    validate={{
                                        required: {
                                            value: false,
                                            errorMessage: t("This field cannot be blank"),
                                        },
                                        email: { errorMessage: t("Your email is invalid") },
                                    }}
                                    id="new-team-person-email"
                                />
                            </Col>

                            <Col md="4" sm="12">
                                <Label for="new-team-person-phone-number">
                                    {t("Phone Number")}
                                </Label>

                                <LocalizedPhoneInput
                                    inputClass="w-100"
                                    inputStyle={{
                                        height  :   '38px'
                                    }}
                                    country={ "us" }
                                    disableInitialCountryGuess={ false }
                                    disableCountryGuess={ false }
                                    onChange={ (e) => {
                                        const input = avFormRef.current?._inputs['person[phoneNumber]'];
                                        if(input){
                                            input.value = e;
                                            input.validate();
                                        }
                                    }}
                                    value={ editingPersonValues && editingPersonValues['person[phoneNumber]'] }
                                />

                                <AvField
                                    name={`person[phoneNumber]`}
                                    type="hidden"
                                    errorMessage={t("This field cannot be blank")}
                                    validate={{
                                        required: { value: false }
                                    }}
                                    id={`new-team-person-phone-number`}
                                />
                            </Col>
                        </Row>
                    </ModalBody>

                    <ModalFooter>
                        <Button onClick={ togglePersonModal } color="primary" type="button" outline>
                            { t('Cancel') }
                        </Button>

                        <Button onClick={ () => avFormRef.current.submit() } disabled={ personFormInProcess } color="primary" type="button" size="md">
                            {
                                personFormInProcess ? (
                                    <Spinner className="me-2" animation="border" variant="white" size="sm"/>
                                ) : (
                                    <>
                                        {
                                            editingPersonValues?.personId ? (
                                                <i className="ri-pencil-line align-middle me-2"></i>
                                            ) : (
                                                <i className="ri-add-line align-middle me-2"></i>
                                            )
                                        }
                                    </>
                                )
                                
                            }
                            { t(editingPersonValues?.personId ? 'Update Person' : 'Add Person') }
                        </Button>
                    </ModalFooter>
                </AvForm>
            </Modal>
        )
    }

    const renderDeletePersonConfirmModal = () => {
        const deleteInProcess = handleDeleteSupplierProductMutation.isLoading || handleDeleteSupplierProductMutation.isFetching;

        return (
            <Modal isOpen={ !!personToDelete } backdrop="static" size="md">
                
                <ModalHeader toggle={() => setPersonToDelete(null)}>
                    {t("Confirmation Form")}
                </ModalHeader>

                <ModalBody>
                    <p>
                        {t('Do you want to delete the person?')}
                    </p>
                </ModalBody>

                <ModalFooter>
                    <Button onClick={() => handleDeleteSupplierProductMutation.mutate(personToDelete.id)} disabled={ deleteInProcess } color="danger" type="button" size="md">
                        {
                            deleteInProcess && (
                                <Spinner className="me-2" animation="border" variant="white" size="sm"/>
                            )
                        }
                        { t('Delete') }
                    </Button>

                    <Button onClick={() => setPersonToDelete(null)} disabled={ deleteInProcess } color="secondary" type="button" outline>
                        { t('Cancel') }
                    </Button>
                </ModalFooter>
            </Modal>
        ) 
    }

    useEffect(() => {
        if(editingPersonValues){
            setShowPersonModal(true);
        }
    }, [ editingPersonValues ]);

    useEffect(() => {
        if(!showPersonModal){
            // reset title on modal close
            setPersonTitle({
                show    :   false,
                value   :   'Mr.'
            })
        }
    }, [showPersonModal]);

    if(teamIsFetching){
        return (
            <LoadingComponent />
        )
    }

    const people = team?.teamManagers || []

    return (
        <div className="contact-people pt-0">
            {/* SPIK 7307 */}
            {false && readOnly && isFeatureEnabledForActiveModule('sanction') && (
                <div className="d-flex justify-content-end align-items-center mb-2">
                    <Button type="button" 
                        color="danger" 
                        disabled={checkingSanctionsInProccess}
                        onClick={mutateSanctionsCheck}
                    >
                            {checkingSanctionsInProccess ? (
                                <>
                                    <Spinner className="me-2" variant="white" size="sm"/>
                                    {t("Checking Sanctions...")}
                                </>
                            ) : (
                                <>
                                    <i className='ri-alert-line me-2' /> 
                                    {t("Check Sanctions")}
                                </>
                            )}
                    </Button>
                </div>
            )}

            <div className="list">
                {!people.length && (
                    <Alert color='warning'>
                        {t(EMPTY_LIST)}
                    </Alert>
                )}

                {people.map((person) => {
                    return (
                        <div className="item mb-3">
                            <Row className="mb-2">
                                <Col sm="12" md="4">
                                    <AIFieldLabel 
                                        id={`tp-team-${person.id}`}
                                        label={`${t('Full Name')}:`}
                                        tooltipText={t('This management team record was generated by AI. It may not have been manually verified and should be reviewed for accuracy.')}
                                        aiGenerated={person.isAiManager}
                                    />
                                    <p>
                                        { `${t(person.title)} ${person.name || ''} ${person.lastName || ''}`.trim() }
                                    </p>
                                </Col>

                                <Col sm="12" md="4">
                                    <Label className="form-label">
                                        {t('Name In Local Language')}:
                                    </Label>
                                    <p>
                                        { `${person.localLanguageFirstName || ''} ${person.localLanguageLastName || ''}`.trim() || '-' }
                                    </p>
                                </Col>

                                <Col sm="12" md="4">
                                    <Label className="form-label">
                                        {t('Position')}:
                                    </Label>
                                    <p>
                                        { person.position }
                                    </p>
                                </Col>
                            </Row>

                            <Row className="mb-2">
                                <Col sm="12" md="4">
                                    <Label className="form-label">
                                        {t('Language')}:
                                    </Label>
                                    <p>
                                        { t(getLanguageName(person.language)) }
                                    </p>
                                </Col>

                                <Col sm="12" md="4">
                                    <Label className="form-label">
                                        {t('Email')}:
                                    </Label>
                                    <p>
                                        { person.email }
                                    </p>
                                </Col>

                                <Col sm="12" md="4">
                                    <Label className="form-label">
                                        {t('Phone Number')}:
                                    </Label>
                                    <LocalizedPhoneInput
                                        readOnly={true}
                                        inputClass="w-100"
                                        country={ "us" }
                                        disableInitialCountryGuess={ false }
                                        disableCountryGuess={ false }
                                        value={person.phoneNumber}
                                    />
                                </Col>
                            </Row>

                            {!readOnly && (
                                <Row>
                                    <Col sm="12" className="d-flex justify-content-end">
                                        <Button 
                                            disabled={handleUpdateSupplierTeamPersonMutation.isFetching} 
                                            onClick={() => {
                                                showTeamPersonEditingForm(person)
                                            }} 
                                            color="primary" type="button" className="me-2" outline>
                                                <i className="ri-pencil-line me-1 align-middle font-size-18" />
                                                {t('Edit Person')}
                                        </Button>

                                        <Button 
                                            disabled={handleDeleteSupplierProductMutation.isFetching} 
                                            onClick={() => {
                                                setPersonToDelete(person)
                                            }} 
                                            color="danger" type="button" outline>
                                                <i className="ri-delete-bin-line me-1 align-middle font-size-18" />
                                                {t('Delete Person')}
                                        </Button>
                                    </Col>
                                </Row>
                            )}
                        </div>
                    )
                })}
            </div>

            {
                !readOnly && (
                    <>
                        <div className="d-flex justify-content-end align-items-center">
                            <Button onClick={() => { setShowPersonModal(true) }} 
                                color="primary" 
                                outline>
                                    <i className="ri-add-line align-middle me-2 font-size-20"></i>
                                    {t('Add A New Person')}
                            </Button>
                        </div>
                        { renderCreateAndEditModals() }
                        { renderDeletePersonConfirmModal() }
                    </>
                )
            }
        </div>
    );
}

const mapStatetoProps = (state) => {
	const { Organization } = state;

	return {
		Organization
	};
};

export default withNamespaces()(connect(
    mapStatetoProps,
    {}
)(ManagementTeam));